import type { AgentWidget, PresetOption } from "@/types/createAgent";
import {
  getCategoryGridActionId,
  getChoicePresets,
  getCommitPresets,
  getPresetPresentationType,
  isGenerationPresetAction,
  isVibeBundlePreset,
  normalizePresetLabel,
  orderPresets,
} from "@/lib/agent/presetPresentation";
import { normalizeWidgetType } from "@/lib/agent/widgetPlan";
import { getWidgetPresets } from "@/lib/agent/widgetPresets";
import { isKnownActionLabel } from "@/lib/agent/labels";

export const MAX_CURATED_PRESET_CHOICES = 3;

/**
 * Form fields whose selected values materially change the front design. Keep
 * workflow flags, upload metadata, auth state, and back-only fields out: a
 * refinement card must leave a durable visual choice, not merely submit a
 * suggest action that changes nothing.
 */
const FRONT_DESIGN_MUTATION_FIELDS = [
  "prompt",
  "color",
  "colorHex",
  "length",
  "sleeve",
  "sleeveAddons",
  "neckline",
  "waistline",
  "silhouette",
  "fabric",
] as const;

function normalizedMutationValue(value: unknown): unknown | undefined {
  if (typeof value === "string") {
    const normalized = value.replace(/\s+/g, " ").trim().toLocaleLowerCase();
    return normalized || undefined;
  }
  if (Array.isArray(value)) {
    const normalized = value
      .map(normalizedMutationValue)
      .filter(item => item !== undefined);
    return normalized.length > 0 ? normalized : undefined;
  }
  return undefined;
}

/**
 * Stable signature for the visual front mutation carried by one preset.
 * Returns null for no-op/navigation-only data.
 */
export function getFrontDesignMutationSignature(data: unknown): string | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const record = data as Record<string, unknown>;
  const mutation = FRONT_DESIGN_MUTATION_FIELDS.flatMap(field => {
    const value = normalizedMutationValue(record[field]);
    return value === undefined ? [] : [[field, value] as const];
  });
  return mutation.length > 0 ? JSON.stringify(mutation) : null;
}

export function hasFrontDesignMutation(data: unknown): boolean {
  return getFrontDesignMutationSignature(data) !== null;
}

/**
 * True when every visual front-design value carried by `mutation` is present
 * with the same normalized value in `projectedForm`.
 *
 * This is deliberately directional: the projected form may contain many other
 * fields from the broader dress direction, while a refinement card normally
 * changes just one of them. It is used when a stale/unsigned click is recovered
 * from a read-only server surface, so merely matching the visible label is not
 * enough to advance the funnel.
 */
export function isFrontDesignMutationReflected(
  mutation: unknown,
  projectedForm: unknown
): boolean {
  if (
    !mutation ||
    typeof mutation !== "object" ||
    Array.isArray(mutation) ||
    !projectedForm ||
    typeof projectedForm !== "object" ||
    Array.isArray(projectedForm)
  ) {
    return false;
  }

  const source = mutation as Record<string, unknown>;
  const target = projectedForm as Record<string, unknown>;
  let foundMutation = false;
  for (const field of FRONT_DESIGN_MUTATION_FIELDS) {
    const expected = normalizedMutationValue(source[field]);
    if (expected === undefined) continue;
    foundMutation = true;
    const actual = normalizedMutationValue(target[field]);
    if (JSON.stringify(actual) !== JSON.stringify(expected)) return false;
  }
  return foundMutation;
}

/** Every visible choice must change the front, and no two may do the same. */
export function hasDistinctFrontDesignMutations(
  presets: readonly Pick<PresetOption, "data">[]
): boolean {
  const signatures = presets.map(preset =>
    getFrontDesignMutationSignature(preset.data)
  );
  return (
    signatures.every((signature): signature is string => signature !== null) &&
    new Set(signatures).size === presets.length
  );
}

/** Process controls are not visual design values, even inside a prompt patch.
 * Keep this a narrow rejection check: adaptive and localized dress details do
 * not need to appear in a fixed vocabulary to count as a refinement. */
export function isWorkflowRefinementChoiceText(value: unknown): boolean {
  if (typeof value !== "string") return false;
  return value.split(/\s*;\s*|(?<=[.!?])\s+|\n+/).some(segment => {
    const text = segment.replace(/\s+/g, " ").trim();
    return (
      isKnownActionLabel(text) ||
      // A saved visual prompt may legitimately start "Render a sage gown".
      // Only complete short workflow commands qualify here, not instruction
      // verbs that introduce an actual garment description.
      /^(?:(?:click|tap|select|choose)\s+)?(?:generate|regenerate|render|rerender|preview)\s+(?:(?:the|a|an|my|your|this|that|first|new|updated)\s+)*(?:(?:front|back)\s+)?(?:preview|image|view|render|it|this|that)(?:\s+(?:now|first|please))?[.!]*$/i.test(
        text
      ) ||
      /^(?:apply\s+(?:the\s+)?changes|get\s+(?:a\s+|the\s+|free\s+)?quote|request\s+(?:a\s+|the\s+)?quote)\b/i.test(
        text
      ) ||
      /^(?:keep|continue|start|resume)\s+(?:chatting|talking|exploring|refining|shaping|designing)(?:\s+(?:the|my|this|some|more|a few))?(?:\s+(?:dress|design|details|options|direction))?(?:\s+(?:first|more|instead|for now))?[.!]*$/i.test(
        text
      ) ||
      /^(?:not (?:yet|now)|maybe later|more (?:options|details|choices)|keep chatting)[.!]*$/i.test(
        text
      )
    );
  });
}

/**
 * Contract for the one widget shown immediately after a broad vibe selection.
 * Every card must be a real choice in the same front-design dimension: two or
 * three suggest actions, one shared action id, and a distinct durable visual
 * mutation on every card. Navigation-only `suggest_field` cards cannot satisfy
 * this contract because clicking them records no design decision.
 */
export function isGroundedFrontRefinementPresetSet(
  presets: readonly Pick<
    PresetOption,
    "action" | "actionId" | "data" | "label" | "presetType"
  >[]
): boolean {
  if (presets.length < 2 || presets.length > 3) return false;
  if (
    presets.some(
      preset =>
        preset.action !== "suggest" ||
        preset.actionId === "suggest_field" ||
        preset.actionId === "set_back_prompt" ||
        preset.actionId === "set_back_style" ||
        preset.presetType === "vibe_bundle" ||
        isWorkflowRefinementChoiceText(preset.label) ||
        (preset.data &&
          typeof preset.data === "object" &&
          FRONT_DESIGN_MUTATION_FIELDS.some(field =>
            isWorkflowRefinementChoiceText(
              (preset.data as Record<string, unknown>)[field]
            )
          ))
    )
  ) {
    return false;
  }
  if (new Set(presets.map(preset => preset.actionId)).size !== 1) {
    return false;
  }
  const normalizedLabels = presets.map(preset =>
    normalizePresetLabel(preset.label)
  );
  if (
    normalizedLabels.some(label => label.length === 0) ||
    new Set(normalizedLabels).size !== presets.length
  ) {
    return false;
  }
  return hasDistinctFrontDesignMutations(presets);
}

function uniquePresets(presets: PresetOption[]): PresetOption[] {
  const seen = new Set<string>();
  return presets.filter(preset => {
    const key = JSON.stringify(preset);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function isBackDesignChoice(preset: PresetOption): boolean {
  return (
    preset.actionId === "set_back_prompt" ||
    preset.actionId === "set_back_style"
  );
}

/**
 * The renderer collapses this authored surface into one front-review decision:
 * quote now, modify locally, or open the back selector locally. Older restored
 * turns used an explicit modify action as the anchor; newer widget plans use a
 * local continue-to-back launcher. Supporting either keeps restoration stable.
 */
export function isFrontReviewDecisionPresetSurface(
  presets: readonly PresetOption[]
): boolean {
  const ordered = orderPresets(presets);
  const commits = getCommitPresets(ordered);
  const choices = getChoicePresets(ordered);
  return (
    choices.length > 0 &&
    choices.every(isBackDesignChoice) &&
    commits.some(preset => preset.actionId === "submit_quote") &&
    commits.some(
      preset =>
        preset.actionId === "modify_dress_front" ||
        preset.actionId === "continue_to_back_design"
    )
  );
}

export function getActiveWidgetSurfacePresets(
  widgets: readonly AgentWidget[]
): PresetOption[] {
  return orderPresets(
    widgets
      .filter(widget => widget.active === true)
      .flatMap(widget => getWidgetPresets(widget))
  );
}

type WidgetPresetLayout = {
  widgetType: ReturnType<typeof normalizeWidgetType>;
  commits: PresetOption[];
  choices: PresetOption[];
  visibleChoices: PresetOption[];
  rendersCatalog: boolean;
  isUpload: boolean;
  isVibe: boolean;
  isCuratedFieldPicker: boolean;
};

function widgetPresetLayout(widget: AgentWidget): WidgetPresetLayout {
  const widgetType = normalizeWidgetType(widget.type);
  const ordered = orderPresets(getWidgetPresets(widget));
  const commits = getCommitPresets(ordered);
  const choices = getChoicePresets(ordered);
  const sharedCategoryActionId = getCategoryGridActionId(choices);
  const rendersCatalog =
    widgetType === "catalog_picker" ||
    Boolean(sharedCategoryActionId && choices.length > 5);
  const isUpload =
    choices.length > 0 &&
    choices.every(
      choice => getPresetPresentationType(choice) === "upload_choice"
    );
  const isVibe = choices.length > 0 && choices.every(isVibeBundlePreset);
  const isCuratedFieldPicker =
    widgetType === "field_picker" && !rendersCatalog && !isUpload;
  const visibleChoices =
    isVibe || isCuratedFieldPicker
      ? choices.slice(0, MAX_CURATED_PRESET_CHOICES)
      : choices;
  return {
    widgetType,
    commits,
    choices,
    visibleChoices,
    rendersCatalog,
    isUpload,
    isVibe,
    isCuratedFieldPicker,
  };
}

/** Presets physically presented inside one widget (including local selectors). */
export function getRendererPresentedWidgetPresets(
  widget: AgentWidget,
  options: { includeDisabled?: boolean } = {}
): PresetOption[] {
  if (widget.active !== true && !options.includeDisabled) return [];

  const layout = widgetPresetLayout(widget);
  const { widgetType } = layout;
  if (widgetType === "brief_summary") return [];

  if (
    widgetType !== "concept_picker" &&
    widgetType !== "field_picker" &&
    widgetType !== "catalog_picker" &&
    widgetType !== "upload_request"
  ) {
    return orderPresets(getWidgetPresets(widget));
  }
  return uniquePresets([...layout.commits, ...layout.visibleChoices]);
}

/**
 * Exact raw widget presets that can produce a server turn. Catalog/concept
 * tiles are local selectors: only their derived Generate/Explore footer
 * payloads are server capabilities. Focused one-tap and upload choices send
 * their raw preset directly, as do action-dock buttons.
 */
export function getRendererDirectWidgetPresets(
  widget: AgentWidget
): PresetOption[] {
  if (widget.active !== true) return [];
  const layout = widgetPresetLayout(widget);
  if (layout.widgetType === "brief_summary") return [];
  if (
    layout.widgetType !== "concept_picker" &&
    layout.widgetType !== "field_picker" &&
    layout.widgetType !== "catalog_picker" &&
    layout.widgetType !== "upload_request"
  ) {
    return orderPresets(getWidgetPresets(widget));
  }

  const directChoices = layout.isUpload
    ? layout.visibleChoices
    : layout.isCuratedFieldPicker && !layout.rendersCatalog && !layout.isVibe
      ? layout.visibleChoices
      : [];
  return uniquePresets([...layout.commits, ...directChoices]);
}

/**
 * Direct widget capabilities after accounting for a multi-widget surface.
 * In the composite front-review UI the back launcher and back cards only
 * change local presentation/selection. Their derived footer actions use
 * message-only provenance, so neither is a direct widget capability.
 */
export function getRendererDirectWidgetPresetsForSurface(
  widget: AgentWidget,
  widgets: readonly AgentWidget[]
): PresetOption[] {
  if (
    !isFrontReviewDecisionPresetSurface(getActiveWidgetSurfacePresets(widgets))
  ) {
    return getRendererDirectWidgetPresets(widget);
  }

  return orderPresets(getWidgetPresets(widget)).filter(
    preset =>
      widget.active === true &&
      (preset.actionId === "submit_quote" ||
        preset.actionId === "start_virtual_try_on" ||
        preset.actionId === "modify_dress_front")
  );
}

/**
 * Direct presets reachable from the widget-free compatibility renderer.
 * Local front-review launchers count as reachable because they reveal their
 * panels without a server turn; standing commits hidden behind a focused
 * one-tap choice do not.
 */
export function getRendererPresentedMessagePresets(
  presets: readonly PresetOption[],
  selectedPresetLabel?: string | null
): PresetOption[] {
  const ordered = orderPresets(presets);
  const commits = getCommitPresets(ordered);
  const choices = getChoicePresets(ordered);
  const selectedChoice =
    choices.find(
      choice =>
        Boolean(selectedPresetLabel) &&
        normalizePresetLabel(choice.label) ===
          normalizePresetLabel(selectedPresetLabel ?? "")
    ) ?? null;

  const sharedCategoryActionId = getCategoryGridActionId(choices);
  const categoryGridActionId =
    sharedCategoryActionId && choices.length > 5
      ? sharedCategoryActionId
      : null;
  const isUpload =
    choices.length > 0 &&
    choices.every(
      choice => getPresetPresentationType(choice) === "upload_choice"
    );
  const isVibe = choices.length > 0 && choices.every(isVibeBundlePreset);

  const backChoices = choices.filter(isBackDesignChoice);
  const isFrontReviewDecisionUi = isFrontReviewDecisionPresetSurface(ordered);

  if (isFrontReviewDecisionUi) {
    return uniquePresets([
      ...commits.filter(preset =>
        ["submit_quote", "start_virtual_try_on", "modify_dress_front"].includes(
          preset.actionId
        )
      ),
      ...backChoices,
    ]);
  }

  const visibleChoices =
    categoryGridActionId || isUpload
      ? choices
      : choices.slice(0, MAX_CURATED_PRESET_CHOICES);
  const oneTapChoiceOwnsTurn = choices.length > 0 && !categoryGridActionId;
  const footerGenerate =
    choices.length > 0
      ? commits.find(preset => isGenerationPresetAction(preset.actionId))
      : undefined;
  const visibleCommits = oneTapChoiceOwnsTurn
    ? []
    : footerGenerate && selectedChoice
      ? commits.filter(preset => preset !== footerGenerate)
      : commits;

  // VibePresetPicker performs the same three-item cap internally.
  const cappedChoices = isVibe
    ? visibleChoices.slice(0, MAX_CURATED_PRESET_CHOICES)
    : visibleChoices;
  return uniquePresets([...visibleCommits, ...cappedChoices]);
}

/** Exact raw message-level presets that can produce a server turn. */
export function getRendererDirectMessagePresets(
  presets: readonly PresetOption[],
  selectedPresetLabel?: string | null
): PresetOption[] {
  const presented = getRendererPresentedMessagePresets(
    presets,
    selectedPresetLabel
  );
  const commits = getCommitPresets(presented);
  const choices = getChoicePresets(presented);

  const allOrdered = orderPresets(presets);
  const allChoices = getChoicePresets(allOrdered);
  const isFrontReviewDecisionUi =
    isFrontReviewDecisionPresetSurface(allOrdered);
  if (isFrontReviewDecisionUi) {
    // Back cards in this panel are local selectors; the reachable commit
    // buttons are direct, while Generate/Explore are derived footer payloads.
    return commits.filter(preset =>
      ["submit_quote", "start_virtual_try_on", "modify_dress_front"].includes(
        preset.actionId
      )
    );
  }

  const sharedCategoryActionId = getCategoryGridActionId(allChoices);
  const rendersCatalog = Boolean(
    sharedCategoryActionId && allChoices.length > 5
  );
  const isVibe = allChoices.length > 0 && allChoices.every(isVibeBundlePreset);
  const isUpload =
    allChoices.length > 0 &&
    allChoices.every(
      choice => getPresetPresentationType(choice) === "upload_choice"
    );
  const directChoices = rendersCatalog || isVibe ? [] : choices;
  return uniquePresets([...commits, ...(isUpload ? choices : directChoices)]);
}

export function getRendererVisibleDirectPresets(params: {
  presets?: PresetOption[];
  widgets?: AgentWidget[];
  selectedPresetLabel?: string | null;
}): PresetOption[] {
  if ((params.widgets ?? []).length > 0) {
    const widgets = params.widgets ?? [];
    return uniquePresets(
      widgets.flatMap(widget =>
        getRendererDirectWidgetPresetsForSurface(widget, widgets)
      )
    );
  }
  return getRendererDirectMessagePresets(
    params.presets ?? [],
    params.selectedPresetLabel
  );
}
